import { Button, FormControl, Input, Paragraph } from '@deckee/deck-hand';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ROUTE_SETTINGS } from '../../../services/route/route-names';
import {
  transitionToNextRoute,
  transitionToPreviousRoute,
} from '../../../services/route/route-navigation';
import {
  requestEmailChange,
  syncEmailChangeReauthFromServer,
} from '../../../services/user/change-email';
import BasicDrawerLayout from '../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../layouts/components/LayoutBody/LayoutBody';
import { trackChangeEmailEvent } from './change-email-events';
import DrawerFlowCard from '../../shared/DrawerFlowCard/DrawerFlowCard';
import DrawerFlowErrorMessage from '../../shared/DrawerFlowCard/components/DrawerFlowErrorMessage';
import DrawerFlowSuccessMessage from '../../shared/DrawerFlowCard/components/DrawerFlowSuccessMessage';

const GENERIC_SUCCESS_MESSAGE =
  'If we can complete this request, check the inbox for your new email address.';

const ChangeEmailFormPage = () => {
  const history = useHistory();
  const [newEmail, setNewEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reauthChecked, setReauthChecked] = useState(false);

  useEffect(() => {
    const checkReauth = async () => {
      try {
        const active = await syncEmailChangeReauthFromServer();
        if (!active) {
          trackChangeEmailEvent('verify reauth', {
            step: 'form',
            outcome: 'error',
          });
          history.replace('/maps/settings/change-email');
        }
      } catch (e) {
        trackChangeEmailEvent('verify reauth', {
          step: 'form',
          outcome: 'error',
        });
        history.replace('/maps/settings/change-email');
      } finally {
        setReauthChecked(true);
      }
    };

    checkReauth();
  }, [history]);

  useEffect(() => {
    if (reauthChecked) {
      trackChangeEmailEvent('view', { step: 'form' });
    }
  }, [reauthChecked]);

  const handleSubmit = async () => {
    trackChangeEmailEvent('send confirmation', { step: 'form' });
    setIsLoading(true);
    setSubmitError(false);
    try {
      await requestEmailChange(newEmail.trim());
      trackChangeEmailEvent('request change', {
        step: 'form',
        outcome: 'success',
      });
      setSubmitted(true);
    } catch (e) {
      trackChangeEmailEvent('request change', {
        step: 'form',
        outcome: 'error',
      });
      setSubmitError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToSettings = () => {
    trackChangeEmailEvent('back to settings', { step: 'form' });
    transitionToNextRoute(ROUTE_SETTINGS);
  };

  if (!reauthChecked) {
    return null;
  }

  return (
    <BasicDrawerLayout
      heading="Enter new email"
      onBack={() => {
        trackChangeEmailEvent('back', { step: 'form' });
        transitionToPreviousRoute();
      }}
      onClose={() => {
        trackChangeEmailEvent('close', { step: 'form' });
        transitionToNextRoute(ROUTE_SETTINGS);
      }}
    >
      <LayoutBody>
        {submitted ? (
          <DrawerFlowCard
            heading="Step 2 of 3"
            actions={
              <Button variant="primary" onClick={handleBackToSettings}>
                Back to Settings
              </Button>
            }
          >
            <DrawerFlowSuccessMessage>
              {GENERIC_SUCCESS_MESSAGE}
            </DrawerFlowSuccessMessage>
            <Paragraph fontSize="1" color="darkergrey">
              When the email arrives at your new address, open the link while
              still signed in to DECKEE to finish the change.
            </Paragraph>
          </DrawerFlowCard>
        ) : (
          <DrawerFlowCard
            heading="Step 2 of 3"
            actions={
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isLoading || !newEmail.trim()}
              >
                {isLoading ? 'Sending…' : 'Send confirmation'}
              </Button>
            }
          >
            <Paragraph mb="3" color="darkergrey">
              Enter the email address you want to use for your DECKEE account.
            </Paragraph>
            {submitError && (
              <DrawerFlowErrorMessage>
                We couldn&apos;t send the confirmation email. Check your
                connection and try again.
              </DrawerFlowErrorMessage>
            )}
            <FormControl label="New email address">
              <Input
                type="email"
                value={newEmail}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setNewEmail(event.target.value)
                }
                autoComplete="email"
                placeholder="name@example.com"
              />
            </FormControl>
          </DrawerFlowCard>
        )}
      </LayoutBody>
    </BasicDrawerLayout>
  );
};

export default ChangeEmailFormPage;
